/**
 * API base URL configuration + per-surface publishable keys.
 *
 * Resolution order for API_BASE_URL (first wins):
 *  1. EXPO_PUBLIC_API_URL environment variable (set in .env or EAS build env)
 *  2. app.json extra.apiUrl (set at build time via eas.json environment)
 *  3. Default: http://localhost:3000  (local dev — backend default port)
 *
 * To point at a remote backend:
 *   EXPO_PUBLIC_API_URL=https://api.beam.example.com npx expo start
 *   — or —
 *   Set extra.apiUrl in app.json / eas.json before building.
 *
 * STRIPE_PUBLISHABLE_KEY (EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY):
 *   The Stripe publishable key is client-safe (it identifies the Stripe account
 *   but cannot perform charge operations). It is stored here for future
 *   in-app PaymentSheet use (when we move from the current Stripe hosted-link
 *   flow to an embedded PaymentSheet). The current WO-M4 flow opens a Stripe
 *   HOSTED link returned by the backend — this key is not used at runtime yet.
 *   NEVER log this value. Set it in .env (gitignored) or EAS build env.
 *   NEVER hardcode Anthropic/Stripe secret/Twilio/SendGrid keys here.
 *   No PII in URLs.
 */

import Constants from 'expo-constants';

function resolveApiUrl(): string {
  // Expo SDK ≥49: EXPO_PUBLIC_* vars are inlined at bundle time — but ONLY via literal dot-notation
  // (`process.env.EXPO_PUBLIC_API_URL`). Bracket access is NOT replaced by the Expo babel transform,
  // so it would silently fall through to the default. Keep this as a direct member access.
  const envVar = process.env.EXPO_PUBLIC_API_URL;
  if (envVar && envVar.length > 0) return envVar;

  // Fallback: app.json extra.apiUrl (set via eas.json env or manually).
  const fromExtra = Constants.expoConfig?.extra?.['apiUrl'] as string | undefined;
  if (fromExtra && fromExtra.length > 0) return fromExtra;

  return 'http://localhost:3000';
}

export const API_BASE_URL = resolveApiUrl();

/**
 * Stripe publishable key — read from EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY.
 * Inlined at bundle time by Expo (EXPO_PUBLIC_* prefix).
 * May be undefined in environments where the variable is not set.
 * NEVER log this value. NEVER use this as a secret key.
 */
export const STRIPE_PUBLISHABLE_KEY: string | undefined =
  process.env.EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY ?? undefined;
